'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import { Header } from '../Header';
import ContentCardGroup from '../ContentCard/ContentCard';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import styles from '@/components-2.0/structure/RenderAsset/renderasset.module.scss';

const ALLOWED_TAGS = ['capp', 'napp', 'papp'];
const DEFAULT_TAG = 'napp';

export default function AppModule(): React.JSX.Element {
    const rawCookie = useSyncExternalStore(
        () => () => {},
        () => document.cookie.match(/sxm_user_info=([^;]+)/)?.[1] ?? '',
        () => null,
    );

    const [content, setContent] = useState<SiteAssetGroup | undefined>();

    useEffect(() => {
        let appInstalled: string | undefined;
        if (rawCookie) {
            try {
                appInstalled = JSON.parse(decodeURIComponent(rawCookie))?.app_installed;
            } catch {
                appInstalled = undefined;
            }
        }

        const tag = appInstalled?.toLowerCase();
        const resolvedTag = tag && ALLOWED_TAGS.includes(tag) ? tag : DEFAULT_TAG;

        const controller = new AbortController();

        fetch(`/rest/app-module/${resolvedTag}`, { signal: controller.signal })
            .then((res) => (res.ok ? res.json() : undefined))
            .then((json) => {
                if (json) setContent(json);
            })
            .catch(() => {});

        return () => controller.abort();
    }, [rawCookie]);

    const assets = content?.fields.assets ?? [];
    const [firstAsset, secondAsset] = assets;

    if (
        !content ||
        assets.length !== 2 ||
        firstAsset?.cms_type !== 'Header' ||
        secondAsset?.cms_type !== 'ContentCard'
    ) {
        return <></>;
    }

    const headerAsset = firstAsset as SiteHeader;
    const contentCardAsset = secondAsset as SiteContentCard;

    const contentCardPresentation = content.presentation?.presentations?.ContentCard;
    const singleCardStyles: SelectedStyles = { ...contentCardPresentation?.styles };
    delete singleCardStyles['Row Type'];

    const contentCardGroupAsset: SiteAssetGroup = {
        ...content,
        fields: { ...content.fields, assets: [contentCardAsset] },
        presentation: {
            groupType: content.presentation?.groupType ?? '',
            presentations: {
                ...content.presentation?.presentations,
                ContentCard: { template: contentCardPresentation?.template ?? '', styles: singleCardStyles },
            },
        },
    };

    return (
        <>
            <div
                className={`standard-molecule-spacing ${styles.anchorTarget}`}
                data-componenttype="Header"
                data-componentid={headerAsset.cms_id}
                id={`header${headerAsset.cms_id}`.toLowerCase()}
            >
                <Header asset={headerAsset} presentation={content.presentation?.presentations['Header']} />
            </div>
            <div
                className={`standard-molecule-spacing ${styles.anchorTarget}`}
                data-componenttype="ContentCard"
                data-componentid={contentCardAsset.cms_id}
                id={`contentcard${contentCardAsset.cms_id}`.toLowerCase()}
            >
                <Grid container>
                    <Grid col={{ lg: 12 }} start={{ lg: 1 }}>
                        <ContentCardGroup asset={contentCardGroupAsset} />
                    </Grid>
                </Grid>
            </div>
        </>
    );
}
